import React, { useState, useMemo, useEffect } from "react";
import useSWR from "swr";
import SkeletonTable from "../../../Common/SkeletonTable";
import ErrorCard from "../../../Common/ErrorCard";
import EmptyCard from "../../../Common/EmptyCard";
import PaginationBar from "../../../Common/PaginationBar";
import { inventoryFetcher } from "../../../../network/InventoryService";
import { DEFAULT_PAGE_SIZE } from "../../../../utils/inventoryHelper";

const SupplierTable = ({
  searchText,
  setShowSupplierCreateModal,
  onSupplierSelect,
}) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      page_index: page - 1,
      page_size: DEFAULT_PAGE_SIZE,
    });

    if (searchText) {
      params.append("search_term", searchText);
    }

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-supplier?${params.toString()}`;
  }, [page, searchText]);

  const {
    data: response,
    error,
    mutate,
  } = useSWR(apiUrl, inventoryFetcher, {
    fallbackData: {},
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 3,
    errorRetryInterval: 1000,
    refreshInterval: 5 * 60 * 1000,
  });

  const supplierData = response?.data || [];
  const totalCount = response?.count || 0;
  const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
  const loading = !response && !error;

  const renderTableContent = () => {
    if (loading) {
      return (
        <SkeletonTable
          columnCount={7}
          rowCount={7}
          headers={[
            "#",
            "Supplier ID",
            "Supplier Name",
            "Supplier Description",
            "Supplier Contact",
            "Status",
            "Action",
          ]}
        />
      );
    }

    if (error) {
      return (
        <ErrorCard
          onRetryClick={mutate}
          title="Error loading suppliers"
          message="Failed to load supplier data. Please try again."
        />
      );
    }

    if (supplierData.length === 0) {
      return (
        <EmptyCard
          title="No suppliers found"
          message={
            searchText
              ? "No suppliers found matching your search criteria."
              : "No supplier data available. Create your first supplier."
          }
        />
      );
    }

    return (
      <table className="inventory-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Supplier ID</th>
            <th>Supplier Name</th>
            <th>Supplier Description</th>
            <th>Supplier Contact</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {supplierData.map((supplier, index) => (
            <tr key={supplier.id || index} className="table-row">
              <td>{(page - 1) * DEFAULT_PAGE_SIZE + index + 1}</td>
              <td>{supplier.unique_id || "-"}</td>
              <td title={supplier.name || ""}>{supplier.name || "-"}</td>
              <td title={supplier.description || ""}>
                {supplier.description || "-"}
              </td>
              <td>{supplier.contact_number || "-"}</td>
              <td>
                <span
                  className={`inventory-status-badge ${
                    supplier.is_active ? "active" : "inactive"
                  }`}
                >
                  {supplier.is_active ? "Active" : "Deactivated"}
                </span>
              </td>
              <td>
                <button
                  className="details-btn"
                  onClick={() => onSupplierSelect(supplier)}
                >
                  Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <div className="inventory-table-container">{renderTableContent()}</div>

      {totalCount > 0 && !loading && !error && (
        <PaginationBar
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
        />
      )}
    </>
  );
};

export default SupplierTable;
